import {evaluateFunction, HideableValue, PartiallyRequired, Values} from '@wix/devzai-utils-common';
import {BymoPageHasSearchProps} from './bymo-page-props';
import {
    BymoTemplateSpeedDialSocialLinksAppearance
} from '../../components/bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial-common';
import {DoppeDtoInnerPageKnownIds} from '../types/doppe-dto-inner-page';


/**
 * Navigation menu item types - discriminated union similar to DoppActionCtaButton
 */
export const BymoPageNavigationMenuSettingsMenuPanelDisplayType = {
    SidePanel: 'SidePanel',
    FullScreen: 'FullScreen',
} as const;

export type BymoPageNavigationMenuSettingsMenuPanelDisplayType = Values<typeof BymoPageNavigationMenuSettingsMenuPanelDisplayType>;


export interface BymoPageNavigationSettings {
    // Navigation always renders as floating button in header
    // layout and position properties removed for simplicity

    // Array of menu items in display order - supports both pages and links
    menuItems: BymoPageNavigationMenuItem[];
    menuSettings: BymoPageHasSearchProps & {
        menuOpeningPanelType: BymoPageNavigationMenuSettingsMenuPanelDisplayType;
        title: HideableValue<string>;
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance;
    }

}

/**
 * Navigation menu item types - discriminated union similar to DoppActionCtaButton
 */
export const BymoPageNavigationMenuItemType = {
    Page: 'Page',
    Link: 'Link',
} as const;
export type BymoPageNavigationMenuItemType = Values<typeof BymoPageNavigationMenuItemType>;

/**
 * Base interface for navigation menu items
 */
export interface BymoPageNavigationMenuItemBase {
    id: string; // Unique identifier for the menu item
    title: string; // Display title in navigation menu
    isVisible: boolean; // Whether this item should be shown in navigation
}

/**
 * Discriminated union for navigation menu item settings
 */
export type BymoPageNavigationMenuItemSettings =
    | BymoPageNavigationMenuItemSettings.Page
    | BymoPageNavigationMenuItemSettings.Link;
export namespace BymoPageNavigationMenuItemSettings {

    type BaseType<T extends BymoPageNavigationMenuItemType> = {
        type: T;
    }

    export type Page = BaseType<typeof BymoPageNavigationMenuItemType.Page> & {
        pageId: string; // ID of the page to navigate to (DoppeDtoInnerPageKnownIds.Home for main page)
    };

    export type Link = BaseType<typeof BymoPageNavigationMenuItemType.Link> & {
        url: string; // External URL to navigate to
        openInNewTab?: boolean; // Whether to open link in new tab
    };
}

/**
 * Complete navigation menu item structure
 */
export interface BymoPageNavigationMenuItem extends BymoPageNavigationMenuItemBase {
    settings: BymoPageNavigationMenuItemSettings;
}

/**
 * Input type for migration - requires id and settings.type, all other fields optional
 */
type BymoPageNavigationMenuItemMigrationInput =
    & Pick<BymoPageNavigationMenuItem, 'id'>
    & Partial<Omit<BymoPageNavigationMenuItem, 'id' | 'settings'>>
    & {
        settings:
            | (PartiallyRequired<BymoPageNavigationMenuItemSettings.Page, 'type'>)
            | (PartiallyRequired<BymoPageNavigationMenuItemSettings.Link, 'type'>)
    };

/**
 * Migrates a navigation menu item from partial data with defaults.
 * Ensures backward compatibility when schema is updated - old data gets filled with new defaults.
 * 
 * @param navigationMenuItem - Partial menu item requiring id and settings.type, other fields optional
 * @returns Complete BymoPageNavigationMenuItem with all required fields filled with defaults
 */
export function bymoPageNavigationMenuItemMigrateFromDefaultValues(
    navigationMenuItem: BymoPageNavigationMenuItemMigrationInput
): BymoPageNavigationMenuItem {

    const settings = evaluateFunction((): BymoPageNavigationMenuItemSettings => {
        switch (navigationMenuItem.settings.type) {
            case BymoPageNavigationMenuItemType.Page: {
                const pageSettings = navigationMenuItem.settings as PartiallyRequired<BymoPageNavigationMenuItemSettings.Page, 'type'>;
                return {
                    type: BymoPageNavigationMenuItemType.Page,
                    pageId: pageSettings.pageId ?? DoppeDtoInnerPageKnownIds.Home,
                };
            }
            case BymoPageNavigationMenuItemType.Link: {
                const linkSettings = navigationMenuItem.settings as PartiallyRequired<BymoPageNavigationMenuItemSettings.Link, 'type'>;
                return {
                    type: BymoPageNavigationMenuItemType.Link,
                    url: linkSettings.url ?? '',
                    openInNewTab: linkSettings.openInNewTab ?? false,
                };
            }
        }
    });

    return {
        id: navigationMenuItem.id,
        title: navigationMenuItem.title ?? '',
        isVisible: navigationMenuItem.isVisible ?? true,
        settings: settings,
    };

}

/**
 * @deprecated Navigation layout is no longer configurable. Always renders as floating button.
 * Kept for backward compatibility only.
 */
export const BymoPageNavigationLayout = {
    /**
     * @deprecated Tab-style navigation is no longer supported
     */
    Tabs: 'Tabs',
    /**
     * Single floating button that opens a menu (default and only option now)
     */
    Button: 'Button',
} as const;
/**
 * @deprecated Navigation layout is no longer configurable.
 */
export type BymoPageNavigationLayout = Values<typeof BymoPageNavigationLayout>;
/**
 * @deprecated Navigation position is no longer configurable. Always renders in header.
 * Kept for backward compatibility only.
 */
export const BymoPageNavigationPosition = {
    /**
     * Navigation appears in the header area (default and only option now)
     */
    Header: 'Header',
    /**
     * @deprecated Footer navigation is no longer supported
     */
    Footer: 'Footer',
    /**
     * @deprecated InContent navigation is no longer supported
     */
    InContent: 'InContent',
} as const;
/**
 * @deprecated Navigation position is no longer configurable.
 */
export type BymoPageNavigationPosition = Values<typeof BymoPageNavigationPosition>;